<template>
  <div class="international-phone-input">
    <div class="phone-field" :class="{ 'error': hasError, 'focused': isFocused }">
      <!-- Country Code Selector (appears as prefix) -->
      <div class="country-selector" @click="toggleDropdown" ref="selectorButton">
        <span v-if="!isCustomCode" :class="`fi fi-${selectedCountry.iso2}`" class="flag-icon"></span>
        <svg v-else class="custom-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
        </svg>
        <span class="country-code">+{{ displayedCode }}</span>
        <svg class="dropdown-arrow" :class="{ 'open': showDropdown }" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      </div>

      <!-- Phone Number Input -->
      <input
        ref="phoneInput"
        v-model="phoneNumber"
        type="tel"
        :placeholder="placeholder"
        class="phone-input"
        @focus="isFocused = true"
        @blur="isFocused = false"
        @input="handleInput"
      />

      <!-- Dropdown Menu -->
      <div v-if="showDropdown" class="dropdown-menu" ref="dropdownMenu">
        <div class="dropdown-search">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search countries..."
            class="search-input"
            @click.stop
          />
        </div>
        <div class="dropdown-list">
          <!-- Custom Country Code Option -->
          <div
            v-if="!searchQuery || 'custom'.includes(searchQuery.toLowerCase())"
            class="dropdown-item custom-item"
            :class="{ 'selected': isCustomCode }"
            @click="showCustomCodeInput"
          >
            <svg class="custom-icon" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            <span class="country-name">Custom Country Code</span>
            <span class="country-code-hint">Enter manually</span>
          </div>

          <div
            v-for="country in filteredCountries"
            :key="country.code"
            class="dropdown-item"
            :class="{ 'selected': !isCustomCode && country.code === selectedCountry.code }"
            @click="selectCountry(country)"
          >
            <span :class="`fi fi-${country.iso2}`" class="flag-icon"></span>
            <span class="country-name">{{ country.name }}</span>
            <span class="country-code">+{{ country.code }}</span>
          </div>
        </div>
      </div>

      <!-- Custom Code Input Modal -->
      <div v-if="showCustomInput" class="custom-code-modal" @click.stop>
        <div class="custom-code-content">
          <h4 class="custom-code-title">Enter Custom Country Code</h4>
          <p class="custom-code-hint">Enter the country code without the + sign (e.g., 1 for USA, 44 for UK)</p>
          <input
            ref="customCodeInput"
            v-model="customCodeValue"
            type="text"
            placeholder="e.g., 1, 44, 971"
            class="custom-code-input"
            @keyup.enter="applyCustomCode"
            @input="validateCustomCode"
          />
          <div class="custom-code-buttons">
            <button @click="cancelCustomCode" class="btn-cancel">Cancel</button>
            <button @click="applyCustomCode" class="btn-apply" :disabled="!customCodeValue">Apply</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { countryCodes, DEFAULT_COUNTRY_CODE } from '@/utils/countryCodes'

export default {
  name: 'InternationalPhoneInput',
  props: {
    modelValue: {
      type: Object,
      default: () => ({
        countryCode: DEFAULT_COUNTRY_CODE,
        phone: ''
      })
    },
    placeholder: {
      type: String,
      default: 'Enter phone number'
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showDropdown: false,
      searchQuery: '',
      isFocused: false,
      phoneNumber: this.modelValue.phone || '',
      selectedCountryCode: this.modelValue.countryCode || DEFAULT_COUNTRY_CODE,
      showCustomInput: false,
      customCodeValue: '',
      isCustomCode: false
    }
  },
  computed: {
    selectedCountry () {
      if (this.isCustomCode) {
        return { code: this.selectedCountryCode, name: 'Custom', iso2: 'xx' }
      }
      return countryCodes.find(c => c.code === this.selectedCountryCode) || countryCodes[0]
    },
    displayedCode () {
      return this.selectedCountryCode
    },
    filteredCountries () {
      if (!this.searchQuery) return countryCodes

      const query = this.searchQuery.toLowerCase()
      return countryCodes.filter(country =>
        country.name.toLowerCase().includes(query) ||
        country.code.includes(query)
      )
    },
    fullPhoneNumber () {
      return `+${this.selectedCountryCode}${this.phoneNumber}`
    }
  },
  watch: {
    modelValue: {
      deep: true,
      handler (newVal) {
        if (newVal.countryCode !== this.selectedCountryCode) {
          this.selectedCountryCode = newVal.countryCode
        }
        if (newVal.phone !== this.phoneNumber) {
          this.phoneNumber = newVal.phone
        }
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount () {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown () {
      this.showDropdown = !this.showDropdown
      this.searchQuery = ''
    },
    selectCountry (country) {
      this.selectedCountryCode = country.code
      this.isCustomCode = false
      this.showDropdown = false
      this.searchQuery = ''
      this.emitValue()
      // Focus back to phone input
      this.$nextTick(() => {
        this.$refs.phoneInput?.focus()
      })
    },
    showCustomCodeInput () {
      this.showDropdown = false
      this.showCustomInput = true
      this.customCodeValue = this.isCustomCode ? this.selectedCountryCode : ''
      this.$nextTick(() => {
        this.$refs.customCodeInput?.focus()
      })
    },
    validateCustomCode () {
      // Remove any non-numeric characters and + sign
      this.customCodeValue = this.customCodeValue.replace(/[^0-9]/g, '')
    },
    applyCustomCode () {
      if (!this.customCodeValue) return

      // Ensure the code doesn't have + sign
      const cleanCode = this.customCodeValue.replace(/\+/g, '')

      this.selectedCountryCode = cleanCode
      this.isCustomCode = true
      this.showCustomInput = false
      this.customCodeValue = ''
      this.emitValue()
      // Focus back to phone input
      this.$nextTick(() => {
        this.$refs.phoneInput?.focus()
      })
    },
    cancelCustomCode () {
      this.showCustomInput = false
      this.customCodeValue = ''
    },
    handleInput () {
      // Remove + sign if user pastes it
      this.phoneNumber = this.phoneNumber.replace(/\+/g, '')
      this.emitValue()
    },
    emitValue () {
      this.$emit('update:modelValue', {
        countryCode: this.selectedCountryCode,
        phone: this.phoneNumber
      })
    },
    handleClickOutside (event) {
      const selector = this.$refs.selectorButton
      const dropdown = this.$refs.dropdownMenu

      if (selector && !selector.contains(event.target) &&
          dropdown && !dropdown.contains(event.target)) {
        this.showDropdown = false
      }
    }
  }
}
</script>

<style scoped>
.international-phone-input {
  width: 100%;
}

.phone-field {
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  transition: all 0.2s;
  position: relative;
  height: 54px;
}

.phone-field.focused {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.phone-field.error {
  border-color: #ef4444;
}

.country-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  cursor: pointer;
  border-right: 1px solid #e5e7eb;
  height: 100%;
  user-select: none;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.country-selector:hover {
  background-color: #f9fafb;
}

.flag-icon {
  width: 24px;
  height: 18px;
  border-radius: 2px;
  flex-shrink: 0;
  object-fit: cover;
}

.country-code {
  font-weight: 600;
  color: #1f2937;
  font-size: 15px;
  min-width: 32px;
}

.dropdown-arrow {
  transition: transform 0.2s;
  color: #6b7280;
  flex-shrink: 0;
}

.dropdown-arrow.open {
  transform: rotate(180deg);
}

.phone-input {
  flex: 1;
  border: none;
  outline: none;
  padding: 0 16px;
  font-size: 15px;
  color: #1f2937;
  background: transparent;
  height: 100%;
}

.phone-input::placeholder {
  color: #9ca3af;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 320px;
  display: flex;
  flex-direction: column;
}

.dropdown-search {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #3b82f6;
}

.dropdown-list {
  overflow-y: auto;
  flex: 1;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.dropdown-item:hover {
  background-color: #f9fafb;
}

.dropdown-item.selected {
  background-color: #eff6ff;
}

.dropdown-item .country-name {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
}

.dropdown-item .country-code {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

/* Scrollbar styling */
.dropdown-list::-webkit-scrollbar {
  width: 6px;
}

.dropdown-list::-webkit-scrollbar-track {
  background: #f9fafb;
}

.dropdown-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.dropdown-list::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Custom Code Styles */
.custom-item {
  border-bottom: 1px solid #e5e7eb;
  background-color: #fefce8;
}

.custom-item:hover {
  background-color: #fef9c3;
}

.custom-item.selected {
  background-color: #fde68a;
}

.custom-icon {
  color: #6b7280;
  flex-shrink: 0;
}

.country-code-hint {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}

/* Custom Code Modal */
.custom-code-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.custom-code-content {
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.custom-code-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.custom-code-hint {
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.custom-code-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  color: #1f2937;
  outline: none;
  transition: border-color 0.2s;
  margin-bottom: 16px;
}

.custom-code-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.custom-code-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-cancel,
.btn-apply {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-cancel {
  background: #f3f4f6;
  color: #4b5563;
}

.btn-cancel:hover {
  background: #e5e7eb;
}

.btn-apply {
  background: #3b82f6;
  color: white;
}

.btn-apply:hover:not(:disabled) {
  background: #2563eb;
}

.btn-apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
